import { aUEC } from '@itc-mod/bank/shared';
import { fmtCount, fmtSCU, measured, type Item, type Lot, type Place } from '@itc-mod/supply/shared';
import { lineText, linesSCU } from './amounts.ts';
import { periodDays, totalsText, type BoardEntry, type BoardPeriod } from './boards.ts';
import { DELIVERY_LABEL, type Delivery } from './incoming.ts';
import { activeByUrgency, requestOrder, type LineProgress, type RequestUnit, type RequestView } from './requests.ts';
import type { Donation, Line, People, Pickup } from './schemas.ts';

// Copy-for-Discord text (plan 2C R14) in Sierra's format: the donor thanks
// (`genDonors`), a request's post (`genPublic`), the open requests (`genRequests`),
// the acquisition list (`genAcquire`), a pickup post (`incPost`) and the incoming
// list (`genIncoming`). Times are Discord timestamps, so each reader sees their own
// time zone; they're stored as UTC instants (ADR 0009).

/** A Discord timestamp: `<t:seconds:F>` (full), `f` (short) or `R` (relative). */
export function stamp(at: Date, style: 'F' | 'f' | 'R'): string {
  return `<t:${String(Math.floor(at.getTime() / 1000))}:${style}>`;
}

/** Names the texts need: people by id, and supply's ships by id. */
export interface TextNames {
  people: People;
  ships: ReadonlyMap<string, string>;
}

/** `genDonors` (line 2253): "Copy donor thanks", from the donors board for the period. Opted-out donors are left out by the caller. */
export function donorThanks(donors: readonly Pick<BoardEntry, 'name' | 'count' | 'totals'>[], period: BoardPeriod): string {
  const days = periodDays(period);
  const lines = [`## Sierra Squadron supply donors${days === null ? '' : ` — last ${String(days)} days`}`, 'Thank you to everyone who donated to Sierra supply. **So others may fight.**', ''];
  if (donors.length === 0) lines.push('No donations logged yet.');
  for (const donor of donors) lines.push(`• **${donor.name}**${donor.count > 1 ? ` (${String(donor.count)} donations)` : ''} — ${totalsText(donor)}`);
  return lines.join('\n');
}

/** `reqAmt`: `100 SCU`, `20 units`, `5,000,000 aUEC`. */
export function requestAmount(amount: number, unit: RequestUnit): string {
  if (unit === 'scu') return `${fmtSCU(amount)} SCU`;
  if (unit === 'auec') return aUEC(amount);
  return `${fmtCount(amount)} units`;
}

/** A place as a post names it: the location, or the ship's name. */
export function placeName(place: Place | null, names: TextNames): string {
  if (place === null) return '';
  return place.kind === 'stockpile' ? place.location : (names.ships.get(place.shipId) ?? 'a ship');
}

const at = (iso: string | null): Date | null => (iso === null ? null : new Date(iso));

/**
 * `reqStockLines` (line 1899): what Sierra holds of each requested item, the
 * stockpile and ships separately. Crates and materials in SCU, the rest in their
 * own unit; items found by name ignoring case (supply's `findItems`).
 */
export function relatedStock(names: readonly string[], items: readonly Item[], lots: readonly Lot[]): string[] {
  return [...new Set(names.filter((name) => name.trim() !== ''))].map((name) => {
    const matching = items.filter((item) => item.name.toLowerCase() === name.toLowerCase());
    let stockpile = 0;
    let ships = 0;
    let unit: 'scu' | 'units' | 'kits' | '' = '';
    for (const item of matching) {
      const own = item.unit === 'crates' || item.unit === 'scu' ? 'scu' : item.unit;
      unit ||= own;
      if (own !== unit) continue;
      for (const lot of lots.filter((candidate) => candidate.item === item.id)) {
        const amount = measured(item.unit, lot);
        if (lot.place.kind === 'ship') ships += amount;
        else stockpile += amount;
      }
    }
    const text = (amount: number) => (unit === 'scu' ? `${fmtSCU(amount)} SCU` : `${fmtCount(amount)} ${unit === 'kits' ? 'kits' : 'units'}`);
    if (stockpile === 0 && ships === 0) return `• ${name} — none on hand`;
    return `• ${name} — ${[stockpile > 0 ? `${text(stockpile)} in the stockpile` : '', ships > 0 ? `${text(ships)} aboard ships` : ''].filter((part) => part !== '').join(', ')}`;
  });
}

export interface RequestPostOptions {
  names: TextNames;
  /** Show what's received and pledged against each line (default true). */
  progress?: boolean;
  /** "related": what's on hand of each item (`stock`); "full": the whole supply report (`report`). */
  attach?: 'none' | 'related' | 'full';
  stock?: readonly string[];
  report?: string;
}

/** `genPublic` (line 1907): one request's post. */
export function requestPost(view: RequestView, options: RequestPostOptions): string {
  const { request } = view;
  const byLine = new Map(view.progress.map((entry) => [entry.lineId, entry]));
  const lines = [`## Donations needed: ${request.title}`, 'Sierra Squadron is collecting donations for this request.'];
  if (request.lines.length > 0) {
    lines.push('', '**Needed**');
    for (const line of request.lines) {
      const done = byLine.get(line.id);
      const parts =
        options.progress !== false && done !== undefined && (done.got > 0 || done.pledged > 0)
          ? [done.got > 0 ? `${requestAmount(done.got, line.unit)} received` : '', done.pledged > 0 ? `${requestAmount(done.pledged, line.unit)} pledged` : '', done.open > 0 ? `${requestAmount(done.open, line.unit)} still needed` : 'covered'].filter((part) => part !== '')
          : [];
      lines.push(`• ${line.name} — ${requestAmount(line.quantity, line.unit)}${parts.length > 0 ? ` (${parts.join(', ')})` : ''}`);
    }
  }
  const neededBy = at(request.neededBy);
  const contact = request.contact !== '' ? request.contact : (options.names.people[request.requester] ?? '');
  const meta = [
    request.deliverTo === null ? '' : `**Drop-off:** ${placeName(request.deliverTo, options.names)}`,
    neededBy === null ? '' : `**Needed by:** ${stamp(neededBy, 'F')} (${stamp(neededBy, 'R')})`,
    request.priority === 'Normal' ? '' : `**Priority:** ${request.priority}`,
    contact === '' ? '' : `**Contact:** ${contact}`,
  ].filter((part) => part !== '');
  if (meta.length > 0) lines.push('', ...meta);
  if (request.notes !== '') lines.push('', request.notes);
  lines.push('', `Reply here or DM ${contact === '' ? 'a Sierra leader' : contact} to donate. Every donation is logged and credited on the Sierra donor board.`);
  if (options.attach === 'related' && (options.stock ?? []).length > 0) lines.push('', '**What Sierra has on hand right now**', ...(options.stock ?? []));
  let out = lines.join('\n');
  if (options.attach === 'full' && options.report !== undefined) out += `\n\n${options.report}`;
  return out;
}

/** A request's lines with what's still needed, `name amount`: `left` (pledges not subtracted) or `open`. */
function stillNeeded(view: RequestView, measure: keyof Pick<LineProgress, 'left' | 'open'>): string {
  const byLine = new Map(view.progress.map((entry) => [entry.lineId, entry]));
  return view.request.lines
    .flatMap((line) => {
      const amount = byLine.get(line.id)?.[measure] ?? line.quantity;
      return amount > 0 ? [`${line.name} ${requestAmount(amount, line.unit)}`] : [];
    })
    .join(', ');
}

/** `genRequests` (line 1922): every Active request, by urgency, with what's open (pledges subtracted). */
export function openRequests(views: readonly RequestView[], names: TextNames): string {
  const active = activeByUrgency(views.map((view) => ({ ...view.request, view }))).map((entry) => entry.view);
  const lines = ['## Sierra Squadron: open donation requests'];
  if (active.length === 0) {
    lines.push('Nothing open right now.');
    return lines.join('\n');
  }
  for (const view of active) {
    const { request } = view;
    const neededBy = at(request.neededBy);
    const contact = request.contact !== '' ? request.contact : (names.people[request.requester] ?? '');
    const left = stillNeeded(view, 'open');
    const meta = [request.deliverTo === null ? '' : ` · drop-off ${placeName(request.deliverTo, names)}`, neededBy === null ? '' : ` · by ${stamp(neededBy, 'R')}`, contact === '' ? '' : ` · contact ${contact}`].join('');
    lines.push('', `**${request.title}**${request.priority === 'Normal' ? '' : ` · ${request.priority}`}`, `Still needed: ${left === '' ? 'covered' : left}${meta}`);
  }
  lines.push('', 'Reply here or DM the contact to donate. Every donation is credited on the Sierra donor board.');
  return lines.join('\n');
}

/** One low stockpile prep level, as supply's `lowStock` call gives it. */
export interface LowStockEntry {
  item: { name: string; category: string; unit: Item['unit'] };
  place: { kind: 'stockpile'; location: string } | { kind: 'ship'; shipId: string };
  have: number;
  target: number;
}

/** "64 SCU", "3 kits", "12": an amount in an item's measure (supply's `measureText`). */
function measureOf(item: Pick<Item, 'unit'>, amount: number): string {
  if (item.unit === 'crates' || item.unit === 'scu') return `${fmtSCU(amount)} SCU`;
  if (item.unit === 'kits') return `${fmtCount(amount)} kit${amount === 1 ? '' : 's'}`;
  return fmtCount(amount);
}

/**
 * `genAcquire` (line 840): Active requests with anything left (pledges not
 * subtracted, as in the panel; Open Question 1), in R3's order, then low stock.
 */
export function acquisitionList(input: { at: Date; requests: readonly RequestView[]; low: readonly LowStockEntry[]; names: TextNames }): string {
  const lines = ['## Sierra acquisition list', `As of ${stamp(input.at, 'f')}`];
  const needed = input.requests
    .filter((view) => view.request.status === 'active')
    .filter((view) => view.request.lines.length === 0 || view.progress.some((entry) => entry.left > 0))
    .sort((a, b) => requestOrder(a.request, b.request));
  if (needed.length > 0) {
    lines.push('', '__Still needed for open requests__');
    for (const view of needed) lines.push(`• **${view.request.title}** — ${stillNeeded(view, 'left')}${view.request.deliverTo === null ? '' : ` → ${placeName(view.request.deliverTo, input.names)}`}`);
  }
  if (input.low.length > 0) {
    lines.push('', '__Low stock__');
    for (const { item, place, have, target } of input.low) {
      const what = item.category === 'Crates' ? `${item.name} crates` : item.name;
      lines.push(`• ${what} — have ${measureOf(item, have)}, minimum ${measureOf(item, target)}${place.kind === 'stockpile' ? ` @ ${place.location}` : ''}`);
    }
  }
  if (needed.length === 0 && input.low.length === 0) lines.push('', 'Nothing to acquire right now.');
  return lines.join('\n');
}

/** An Incoming card's parts, for a pledge or a delivery. */
interface Incoming {
  title: string;
  kind: string;
  lines: readonly Line[];
  pickup: Pickup | null;
  place: Place | null;
}

/** An Incoming card, with its donation's title as the page shows it. */
export type IncomingText = { kind: 'donation'; donation: Donation; from: string } | { kind: 'delivery'; delivery: Delivery };

export function incomingOf(entry: IncomingText): Incoming {
  if (entry.kind === 'donation') return { title: entry.from, kind: 'Donation', lines: entry.donation.lines, pickup: entry.donation.pickup, place: entry.donation.place };
  return { title: entry.delivery.from, kind: DELIVERY_LABEL[entry.delivery.kind], lines: entry.delivery.lines, pickup: entry.delivery.pickup, place: entry.delivery.place };
}

export const STAGE: Readonly<Record<Pickup['stage'], string>> = { pledged: 'Pledged', scheduled: 'Scheduled', in_transit: 'In transit' };

export function haulerOf(pickup: Pickup | null, names: TextNames): string {
  if (pickup?.ship == null) return '';
  return 'name' in pickup.ship ? pickup.ship.name : (names.ships.get(pickup.ship.shipId) ?? 'a ship');
}

/** `incPost` (line 2054): a pickup's post. "Pickup: Donation from Vex", "Pickup: Purchase from Hurston Dynamics". */
export function pickupPost(entry: IncomingText, names: TextNames): string {
  const card = incomingOf(entry);
  const load = linesSCU(card.lines);
  const when = at(card.pickup?.at ?? null);
  const collector = card.pickup?.collector == null ? '' : (names.people[card.pickup.collector] ?? '');
  const hauler = haulerOf(card.pickup, names);
  const to = placeName(card.place, names);
  return [
    `## Pickup: ${card.kind} from ${card.title}`,
    card.lines.filter((line) => line.quantity > 0).map((line) => `• ${lineText(line)}`).join('\n'),
    load > 0 ? `**Load:** ≈${fmtSCU(load)} SCU` : '',
    card.pickup?.from ? `**Collect at:** ${card.pickup.from}` : '',
    when === null ? '**When:** to be scheduled' : `**When:** ${stamp(when, 'F')} (${stamp(when, 'R')})`,
    hauler === '' ? '' : `**Hauling with:** ${hauler}`,
    collector === '' ? '' : `**Collector:** ${collector}`,
    to === '' ? '' : `**Delivering to:** ${to}`,
    card.pickup?.plan ? `\n${card.pickup.plan}` : '',
    'Need an escort or extra hauler? Reply here.',
  ]
    .filter((part) => part !== '')
    .join('\n');
}

/** `genIncoming` (line 2065): everything open, soonest pickup first. */
export function incomingList(entries: readonly IncomingText[], input: { at: Date; names: TextNames }): string {
  const lines = ['## Sierra incoming deliveries', `${String(entries.length)} open as of ${stamp(input.at, 'f')}`];
  for (const entry of entries) {
    const card = incomingOf(entry);
    const when = at(card.pickup?.at ?? null);
    const load = linesSCU(card.lines);
    const collector = card.pickup?.collector == null ? '' : (input.names.people[card.pickup.collector] ?? '');
    lines.push(
      '',
      `**${card.title}** — ${card.kind} · ${STAGE[card.pickup?.stage ?? 'pledged']}`,
      ...card.lines.filter((line) => line.quantity > 0).map((line) => `• ${lineText(line)}`),
      [card.pickup?.from ? `Collect at ${card.pickup.from}` : '', when === null ? '' : stamp(when, 'f'), collector === '' ? '' : `collector ${collector}`, load > 0 ? `≈${fmtSCU(load)} SCU` : ''].filter((part) => part !== '').join(' · '),
    );
  }
  if (entries.length === 0) lines.push('', 'Nothing incoming.');
  return lines.join('\n');
}
